import React, { Component } from 'react';
import { ErrorCategory, ErrorSeverity } from '../../utils/errorHandling/errorTypes';
import { handleError } from '../../utils/errorHandling/errorService';
import { isMonacoCancellationError } from '../../utils/monacoCancellationFilter';
import './ErrorBoundary.css';

/**
 * Error Boundary Component
 * 
 * A React component that catches JavaScript errors anywhere in its child component tree,
 * logs those errors, and displays a fallback UI instead of crashing the whole app.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    if (isMonacoCancellationError(error)) {
      return { hasError: false, error: null, errorInfo: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (isMonacoCancellationError(error)) {
      return;
    }
    // Log the error to our centralized error handling service
    handleError(error, {
      category: ErrorCategory.RENDERING,
      severity: ErrorSeverity.ERROR,
      context: {
        componentStack: errorInfo.componentStack,
        component: this.props.componentName || 'Unknown Component'
      }
    });

    this.setState({ errorInfo });
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  }

  render() {
    const { hasError, error } = this.state;
    const { fallback, children } = this.props;

    if (hasError) {
      // If a custom fallback is provided, use it
      if (fallback) {
        return typeof fallback === 'function'
          ? fallback(error, this.resetError)
          : fallback;
      }

      // Default fallback UI
      return (
        <div className="error-boundary-fallback">
          <h2>Something went wrong</h2>
          <p>We're sorry, but there was a problem loading this part of the page.</p>
          <button
            onClick={this.resetError}
            className="error-boundary-retry-btn"
          >
            Try Again
          </button>
        </div>
      );
    }

    // When there's no error, render children normally
    return children;
  }
}

/**
 * Higher-order component that wraps a component with an ErrorBoundary
 * 
 * @param {React.Component} Component - The component to wrap
 * @param {Object} options - Options for the ErrorBoundary
 * @returns {React.Component} Wrapped component with error boundary
 */
export const withErrorBoundary = (Component, options = {}) => {
  const { fallback, componentName = Component.displayName || Component.name } = options;

  const WrappedComponent = (props) => (
    <ErrorBoundary fallback={fallback} componentName={componentName}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `WithErrorBoundary(${componentName})`;
  return WrappedComponent;
};

export default ErrorBoundary;
